import { KEYBOARD_SHORTCUTS } from '@bangle.io/constants';
import { useCoreServices } from '@bangle.io/context';
import {
  DropdownMenu,
  KbdShortcut,
  Sidebar,
  snowflakeIconUrl,
} from '@bangle.io/ui-components';
import { useSetAtom } from 'jotai';
import {
  Command,
  ExternalLink,
  Folder,
  PlusIcon,
  Search,
  Settings2,
  Waypoints,
} from 'lucide-react';
import React from 'react';

/**
 * The dropdown content for the sidebar footer: creation shortcuts, app
 * actions, and external links. Extracted from AppSidebar so the sidebar
 * component stays a thin composition layer.
 */
export function SidebarFooterMenu({
  canCreateFiles,
}: {
  canCreateFiles: boolean;
}) {
  const { commandDispatcher, workbenchState } = useCoreServices();
  const setOpenOmniSearch = useSetAtom(workbenchState.$openOmniSearch);

  return (
    <>
      <DropdownMenu.DropdownMenuLabel className="text-muted-foreground text-xs">
        {t.app.sidebar.newLabel}
      </DropdownMenu.DropdownMenuLabel>
      <DropdownMenu.DropdownMenuItem
        disabled={!canCreateFiles}
        onClick={() => {
          if (!canCreateFiles) {
            return;
          }
          commandDispatcher.dispatch(
            'command::ui:create-note-dialog',
            {
              prefillName: undefined,
            },
            'ui',
          );
        }}
      >
        <PlusIcon className="mr-2 h-4 w-4" />
        <span>{t.app.common.newNote}</span>
      </DropdownMenu.DropdownMenuItem>
      <DropdownMenu.DropdownMenuItem
        onClick={() =>
          commandDispatcher.dispatch(
            'command::ui:create-workspace-dialog',
            null,
            'ui',
          )
        }
      >
        <Folder className="mr-2 h-4 w-4" />
        <span>{t.app.common.newWorkspace}</span>
      </DropdownMenu.DropdownMenuItem>

      <DropdownMenu.DropdownMenuSeparator />
      <DropdownMenu.DropdownMenuLabel className="text-muted-foreground text-xs">
        {t.app.sidebar.appActionsLabel}
      </DropdownMenu.DropdownMenuLabel>
      <DropdownMenu.DropdownMenuItem onClick={() => setOpenOmniSearch(true)}>
        <Search className="mr-2 h-4 w-4" />
        <span>{t.app.sidebar.omniSearch}</span>
        <KbdShortcut
          className="ml-auto"
          keys={KEYBOARD_SHORTCUTS.toggleOmniSearch.keys}
        />
      </DropdownMenu.DropdownMenuItem>
      <DropdownMenu.DropdownMenuItem
        onClick={() => {
          commandDispatcher.dispatch('command::ws:go-graph', null, 'ui');
        }}
      >
        <Waypoints className="mr-2 h-4 w-4" />
        <span>{t.app.graph.title}</span>
      </DropdownMenu.DropdownMenuItem>
      <DropdownMenu.DropdownMenuItem
        onClick={() => workbenchState.goToCommandRoute()}
      >
        <Command className="mr-2 h-4 w-4" />
        <span>{t.app.sidebar.allCommands}</span>
        <KbdShortcut
          className="ml-auto"
          keys={KEYBOARD_SHORTCUTS.openCommands.keys}
        />
      </DropdownMenu.DropdownMenuItem>
      <SettingsMenuItem
        onOpenSettings={() =>
          commandDispatcher.dispatch('command::ui:open-settings', null, 'ui')
        }
      />

      <DropdownMenu.DropdownMenuSeparator />
      <DropdownMenu.DropdownMenuLabel className="text-muted-foreground text-xs">
        {t.app.sidebar.linksLabel}
      </DropdownMenu.DropdownMenuLabel>
      <DropdownMenu.DropdownMenuItem
        onClick={() => window.open('https://jpsariz.com', '_blank')}
      >
        <img
          src={snowflakeIconUrl}
          alt=""
          className="mr-2 h-4 w-4 invert dark:invert-0"
        />
        <span>jpsariz.com</span>
      </DropdownMenu.DropdownMenuItem>
      <DropdownMenu.DropdownMenuItem
        onClick={() => window.open('/login', '_self')}
      >
        <ExternalLink className="mr-2 h-4 w-4" />
        <span>Sign In</span>
      </DropdownMenu.DropdownMenuItem>
    </>
  );
}

function SettingsMenuItem({ onOpenSettings }: { onOpenSettings: () => void }) {
  const { isMobile, setOpenMobile } = Sidebar.useSidebar();

  return (
    <DropdownMenu.DropdownMenuItem
      onClick={() => {
        if (isMobile) {
          setOpenMobile(false);
        }
        onOpenSettings();
      }}
    >
      <Settings2 className="mr-2 h-4 w-4" />
      <span>{t.app.sidebar.settings}</span>
    </DropdownMenu.DropdownMenuItem>
  );
}
